import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import {
  Plus,
  Robot,
  Sparkle,
  UsersThree,
  WarningCircle
} from '@phosphor-icons/react';

// Custom node component for org chart roles
const RoleNode = ({ data }) => {
  const hasAgent = data.hasAgent;
  const hasOpportunity = data.hasOpportunity;
  
  // Note: Button is now display-only since we can't safely pass functions through React Flow
  // The actual create agent functionality will be handled via the grid view below
  
  return (
    <div 
      className={`px-4 py-3 rounded-lg border-2 shadow-lg min-w-[180px] transition-all ${
        hasAgent 
          ? 'bg-gradient-to-br from-[#6366F1]/20 to-[#8B5CF6]/20 border-[#6366F1]' 
          : hasOpportunity
          ? 'bg-[rgba(255,255,255,0.05)] border-[#F59E0B]'
          : 'bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)]'
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1">
          <div className="text-sm font-bold text-white mb-1">{data.name}</div>
          {data.department && (
            <div className="text-xs text-[#A0AEC0]">{data.department}</div>
          )}
        </div>
        <div className="flex gap-1">
          {hasAgent && (
            <div className="w-5 h-5 rounded-full bg-[#6366F1] flex items-center justify-center" title="Has AI Agent">
              <Robot size={12} className="text-white" />
            </div>
          )}
          {hasOpportunity && !hasAgent && (
            <div className="w-5 h-5 rounded-full bg-[#F59E0B] flex items-center justify-center" title="AI Opportunity">
              <Sparkle size={12} className="text-white" />
            </div>
          )}
        </div>
      </div>
      
      {data.description && (
        <div className="text-xs text-[#718096] mb-2 line-clamp-2">{data.description}</div>
      )}
      
      {data.aiOpportunity && (
        <div className="text-xs text-[#A0AEC0] mt-2 italic">"{data.aiOpportunity}"</div>
      )}
    </div>
  );
};

const nodeTypes = {
  roleNode: RoleNode,
};

// Auto-layout using dagre
const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  const dagreNetwork = new dagre.graphlib.Network();
  dagreNetwork.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction, nodesep: 100, ranksep: 150 });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 200, height: 100 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.position = {
      x: nodeWithPosition.x - 100,
      y: nodeWithPosition.y - 50,
    };
  });

  return { nodes, edges };
};

const InteractiveOrgChart = ({ parsedData, onCreateAgent, existingAgents = [] }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);

  // Convert parsed data to React Flow nodes and edges
  useEffect(() => {
    if (!parsedData || !parsedData.roles) return;

    console.log('Processing org chart data:', parsedData);
    const roles = parsedData.roles;
    
    // Validate roles is array
    if (!Array.isArray(roles)) {
      console.error('Roles is not an array:', roles);
      return;
    }
    
    // Create agent lookup map
    const agentMap = {};
    if (existingAgents && Array.isArray(existingAgents)) {
      existingAgents.forEach(agent => {
        if (agent.linked_role_id) {
          agentMap[agent.linked_role_id] = agent;
        }
      });
    }

    // Create nodes - only pass serializable data (NO FUNCTIONS!)
    const newNodes = roles.map(role => {
      const roleId = String(role.id);
      return {
        id: roleId,
        type: 'roleNode',
        data: {
          id: roleId,
          name: String(role.name || 'Untitled Role'),
          department: String(role.department || ''),
          description: String(role.description || ''),
          level: Number(role.level) || 0,
          hasAgent: !!agentMap[role.id],
          hasOpportunity: !!role.ai_opportunity,
          aiOpportunity: String(role.ai_opportunity || '')
          // NOTE: onCreateAgent function removed - will be handled via node click
        },
        position: { x: 0, y: 0 },
        draggable: true
      };
    });

    // Create edges
    const newEdges = roles
      .filter(role => role.parent_id && role.parent_id !== 'null' && role.parent_id !== null)
      .map(role => ({
        id: `e-${String(role.parent_id)}-${String(role.id)}`,
        source: String(role.parent_id),
        target: String(role.id),
        type: 'smoothstep',
        animated: false,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: '#6366F1',
        },
        style: { stroke: '#6366F1', strokeWidth: 2 },
      }));

    // Apply layout
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      newNodes,
      newEdges
    );

    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }, [parsedData, existingAgents, onCreateAgent, setNodes, setEdges]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  if (!parsedData || !parsedData.roles || parsedData.roles.length === 0) {
    return (
      <div className="h-[600px] flex items-center justify-center glass rounded-xl">
        <div className="text-center">
          <WarningCircle size={48} className="mx-auto mb-4 text-[#A0AEC0]" />
          <p className="text-white text-lg">No org chart data loaded</p>
          <p className="text-sm text-[#A0AEC0] mt-2">Parse an uploaded image to get started</p>
        </div>
      </div>
    );
  }

  // Validate data structure
  try {
    if (!Array.isArray(parsedData.roles)) {
      console.error('Invalid roles data:', parsedData.roles);
      return (
        <div className="h-[600px] flex items-center justify-center glass rounded-xl">
          <div className="text-center">
            <WarningCircle size={48} className="mx-auto mb-4 text-[#EF4444]" />
            <p className="text-white text-lg">Invalid org chart data</p>
            <p className="text-sm text-[#A0AEC0] mt-2">Please try parsing the image again</p>
          </div>
        </div>
      );
    }
  } catch (error) {
    console.error('Error validating org chart data:', error);
    return (
      <div className="h-[600px] flex items-center justify-center glass rounded-xl">
        <div className="text-center">
          <WarningCircle size={48} className="mx-auto mb-4 text-[#EF4444]" />
          <p className="text-white text-lg">Error loading org chart</p>
          <p className="text-sm text-[#A0AEC0] mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[600px] glass rounded-xl overflow-hidden border border-[rgba(255,255,255,0.1)]">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        fitView
        attributionPosition="bottom-left"
      >
        <Controls />
        <MiniMap 
          nodeColor={(node) => {
            if (node.data.hasAgent) return '#6366F1';
            if (node.data.hasOpportunity) return '#F59E0B';
            return '#374151';
          }}
          maskColor="rgba(0, 0, 0, 0.6)"
        />
        <Background color="#6366F1" gap={16} />
      </ReactFlow>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 glass rounded-lg p-3 border border-[rgba(255,255,255,0.1)]">
        <div className="text-xs font-semibold text-white mb-2">Legend</div>
        <div className="space-y-1 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-[#6366F1]"></div>
            <span className="text-[#A0AEC0]">Has AI Agent</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-[#F59E0B]"></div>
            <span className="text-[#A0AEC0]">AI Opportunity</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-[#374151]"></div>
            <span className="text-[#A0AEC0]">No Agent</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="absolute top-4 right-4 glass rounded-lg p-3 border border-[rgba(255,255,255,0.1)]">
        <div className="text-xs font-semibold text-white mb-2">Org Chart Stats</div>
        <div className="space-y-1 text-xs">
          <div className="flex items-center justify-between gap-4">
            <span className="text-[#A0AEC0]">Total Roles:</span>
            <span className="text-white font-semibold">{parsedData.roles?.length || 0}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-[#A0AEC0]">Departments:</span>
            <span className="text-white font-semibold">{parsedData.departments?.length || 0}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-[#A0AEC0]">Agents Active:</span>
            <span className="text-white font-semibold">{existingAgents.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveOrgChart;
